import type { SerializedNode, ConversionContext } from '../types';
import { getAttribute } from '../parser';
import {
  parseColor,
  parsePadding,
  parseBorderRadius,
  parseBoolean,
  parseInteger,
  parseFloat_,
  parsePosition,
  parsePositionOffset,
  inferPositionAnchorsState,
  parseShadowAttributes,
  SHADOW_ATTRIBUTES,
} from '../utils';
import { VIDEO_DEFAULTS, getLeafFillSpace } from '../utils/defaults';
import { VIDEO_NODE_NAME } from '../../utils/constants';

/**
 * Transform a <video> XML element into a CraftJS Video node
 *
 * @param element - The XML element
 * @param context - Conversion context
 * @returns SerializedNode for the Video
 */
export const transformVideo = (element: Element, context: ConversionContext): SerializedNode => {
  // Parse required url
  const urlAttr = getAttribute(element, 'url');
  const url = urlAttr || '';

  // Parse provider (youtube, vimeo)
  const providerAttr = getAttribute(element, 'provider');
  const videoProvider = mapVideoProvider(providerAttr) || VIDEO_DEFAULTS.videoProvider;

  // Parse playback options
  const autoplayAttr = getAttribute(element, 'autoplay');
  const autoplay = parseBoolean(autoplayAttr, VIDEO_DEFAULTS.autoplay);

  const loopAttr = getAttribute(element, 'loop');
  const loop = parseBoolean(loopAttr, VIDEO_DEFAULTS.loop);

  const muteAttr = getAttribute(element, 'mute');
  const mute = parseBoolean(muteAttr, VIDEO_DEFAULTS.mute);

  const controlsAttr = getAttribute(element, 'controls');
  const controls = parseBoolean(controlsAttr, VIDEO_DEFAULTS.controls);

  // Parse dimensions
  const widthAttr = getAttribute(element, 'width');
  const width = widthAttr || VIDEO_DEFAULTS.width;

  const heightAttr = getAttribute(element, 'height');
  const height = heightAttr || VIDEO_DEFAULTS.height;

  // Parse spacing
  const paddingAttr = getAttribute(element, 'padding');
  const padding = parsePadding(paddingAttr, VIDEO_DEFAULTS.padding);

  const marginAttr = getAttribute(element, 'margin');
  const margin = parsePadding(marginAttr, VIDEO_DEFAULTS.margin);

  // Parse border
  const borderRadiusAttr = getAttribute(element, 'borderRadius');
  const borderRadius = parseBorderRadius(borderRadiusAttr, VIDEO_DEFAULTS.borderRadius);

  const borderWidthAttr = getAttribute(element, 'borderWidth');
  const borderWidth = parseInteger(borderWidthAttr, VIDEO_DEFAULTS.borderWidth);

  const borderColorAttr = getAttribute(element, 'borderColor');
  const borderColor = borderColorAttr ? parseColor(borderColorAttr) : undefined;

  // Parse opacity
  const opacityAttr = getAttribute(element, 'opacity');
  const opacity = parseFloat_(opacityAttr, VIDEO_DEFAULTS.opacity);

  // Parse background color
  const bgAttr = getAttribute(element, 'bg');
  const backgroundColor = bgAttr ? parseColor(bgAttr) : undefined;

  // Parse position attributes
  const positionAttr = getAttribute(element, 'position');
  const position = parsePosition(positionAttr) || VIDEO_DEFAULTS.position;

  const topAttr = getAttribute(element, 'top');
  const top = parsePositionOffset(topAttr);

  const leftAttr = getAttribute(element, 'left');
  const left = parsePositionOffset(leftAttr);

  const rightAttr = getAttribute(element, 'right');
  const right = parsePositionOffset(rightAttr);

  const bottomAttr = getAttribute(element, 'bottom');
  const bottom = parsePositionOffset(bottomAttr);

  const zIndexAttr = getAttribute(element, 'zIndex');
  const zIndex = parseInteger(zIndexAttr, VIDEO_DEFAULTS.zIndex);

  // Infer position anchors state from provided values
  const positionAnchorsState = inferPositionAnchorsState(position, top, left, right, bottom);

  // Parse shadow attributes
  const shadow = parseShadowAttributes(element);

  // Parse breakpoint overrides
  const tablet = parseBreakpointOverrides(element, 'tablet');
  const mobile = parseBreakpointOverrides(element, 'mobile');

  // Build props object
  const props: Record<string, unknown> = {
    sourceType: VIDEO_DEFAULTS.sourceType,
    url,
    embedCode: VIDEO_DEFAULTS.embedCode,
    videoProvider,
    thumbnailUrl: VIDEO_DEFAULTS.thumbnailUrl,
    autoplay,
    loop,
    mute,
    controls,
    fitType: VIDEO_DEFAULTS.fitType,
    width,
    height,
    padding,
    margin,
    borderRadius,
    borderWidth,
    opacity,
    // Position props
    position,
    top: top || VIDEO_DEFAULTS.top,
    left: left || VIDEO_DEFAULTS.left,
    right: right || VIDEO_DEFAULTS.right,
    bottom: bottom || VIDEO_DEFAULTS.bottom,
    positionAnchorsState,
    zIndex,
    minWidth: VIDEO_DEFAULTS.minWidth,
    minHeight: VIDEO_DEFAULTS.minHeight,
    fillSpace: getLeafFillSpace(context.parentFlexDirection, VIDEO_DEFAULTS.fillSpace),
    flexBasis: VIDEO_DEFAULTS.flexBasis,
    flexGrow: VIDEO_DEFAULTS.flexGrow,
    visibility: VIDEO_DEFAULTS.visibility,
    aspectRatioLock: VIDEO_DEFAULTS.aspectRatioLock,
    tablet: { ...VIDEO_DEFAULTS.tablet, ...tablet },
    mobile: { ...VIDEO_DEFAULTS.mobile, ...mobile },
  };

  // Only include optional props if they have values
  if (borderColor) {
    props.borderColor = borderColor;
  }
  if (backgroundColor) {
    props.backgroundColor = backgroundColor;
  }
  if (shadow) {
    props.shadowType = shadow.shadowType;
    props.shadowSettings = shadow.shadowSettings;
  }

  return {
    type: { resolvedName: VIDEO_NODE_NAME },
    isCanvas: false,
    props,
    displayName: VIDEO_NODE_NAME,
    custom: {
      displayName: VIDEO_NODE_NAME,
      isLocked: false,
    },
    hidden: false,
    nodes: [],
    linkedNodes: {},
    parent: context.parentId,
  };
};

/**
 * Map provider attribute to CraftJS videoProvider value
 */
const mapVideoProvider = (value: string | null): 'youtube' | 'vimeo' | 'wistia' | undefined => {
  if (!value) return undefined;

  const normalized = value.toLowerCase().trim();
  switch (normalized) {
    case 'youtube':
      return 'youtube';
    case 'vimeo':
      return 'vimeo';
    case 'wistia':
      return 'wistia';
    default:
      return undefined;
  }
};

/**
 * Parse breakpoint-specific overrides (tablet-* or mobile-* prefixed attributes)
 */
const parseBreakpointOverrides = (
  element: Element,
  breakpoint: 'tablet' | 'mobile',
): Record<string, unknown> => {
  const overrides: Record<string, unknown> = {};

  // width
  const widthAttr = getAttribute(element, `${breakpoint}-width`);
  if (widthAttr) overrides.width = widthAttr;

  // height
  const heightAttr = getAttribute(element, `${breakpoint}-height`);
  if (heightAttr) overrides.height = heightAttr;

  // padding
  const paddingAttr = getAttribute(element, `${breakpoint}-padding`);
  if (paddingAttr) overrides.padding = parsePadding(paddingAttr, [0, 0, 0, 0]);

  // margin
  const marginAttr = getAttribute(element, `${breakpoint}-margin`);
  if (marginAttr) overrides.margin = parsePadding(marginAttr, [0, 0, 0, 0]);

  // borderRadius
  const borderRadiusAttr = getAttribute(element, `${breakpoint}-borderRadius`);
  if (borderRadiusAttr) overrides.borderRadius = parseBorderRadius(borderRadiusAttr, [0, 0, 0, 0]);

  // Position attributes
  const positionAttr = getAttribute(element, `${breakpoint}-position`);
  if (positionAttr) {
    const parsedPosition = parsePosition(positionAttr);
    if (parsedPosition) overrides.position = parsedPosition;
  }

  const topAttr = getAttribute(element, `${breakpoint}-top`);
  const leftAttr = getAttribute(element, `${breakpoint}-left`);
  const rightAttr = getAttribute(element, `${breakpoint}-right`);
  const bottomAttr = getAttribute(element, `${breakpoint}-bottom`);

  const top = parsePositionOffset(topAttr);
  const left = parsePositionOffset(leftAttr);
  const right = parsePositionOffset(rightAttr);
  const bottom = parsePositionOffset(bottomAttr);

  if (top !== undefined) overrides.top = top;
  if (left !== undefined) overrides.left = left;
  if (right !== undefined) overrides.right = right;
  if (bottom !== undefined) overrides.bottom = bottom;

  // Infer positionAnchorsState if any position props are provided
  const hasPositionOverride = positionAttr || topAttr || leftAttr || rightAttr || bottomAttr;
  if (hasPositionOverride) {
    const breakpointPosition = parsePosition(positionAttr);
    overrides.positionAnchorsState = inferPositionAnchorsState(
      breakpointPosition,
      top,
      left,
      right,
      bottom,
    );
  }

  const zIndexAttr = getAttribute(element, `${breakpoint}-zIndex`);
  if (zIndexAttr) overrides.zIndex = parseInteger(zIndexAttr, 0);

  return overrides;
};

/**
 * Valid attributes for video elements
 */
export const VIDEO_ATTRIBUTES = [
  'url',
  'provider',
  'autoplay',
  'loop',
  'mute',
  'controls',
  'width',
  'height',
  'padding',
  'margin',
  'borderRadius',
  'borderWidth',
  'borderColor',
  'opacity',
  'bg',
  // Shadow attributes
  ...SHADOW_ATTRIBUTES,
  // Position attributes
  'position',
  'top',
  'left',
  'right',
  'bottom',
  'zIndex',
  // Tablet breakpoint overrides
  'tablet-width',
  'tablet-height',
  'tablet-padding',
  'tablet-margin',
  'tablet-borderRadius',
  'tablet-position',
  'tablet-top',
  'tablet-left',
  'tablet-right',
  'tablet-bottom',
  'tablet-zIndex',
  // Mobile breakpoint overrides
  'mobile-width',
  'mobile-height',
  'mobile-padding',
  'mobile-margin',
  'mobile-borderRadius',
  'mobile-position',
  'mobile-top',
  'mobile-left',
  'mobile-right',
  'mobile-bottom',
  'mobile-zIndex',
] as const;

/**
 * Check if an attribute is valid for video elements
 */
export const isValidVideoAttribute = (attr: string): boolean => {
  return (VIDEO_ATTRIBUTES as readonly string[]).includes(attr);
};
